// The fixed flag popover shown when hovering flagged content — the same
// card InvestigationTranscript draws for flagged segments, shared by the
// spreadsheet grid, the PDF and photo viewers, and the video marker bar.
// `corner` renders the top-right anchor label (timestamp, row count, page
// number). Items are either investigation leads (`leads`) or the generic
// highlight shape (`items`) the video surfaces produce, which can carry a
// thumbnail of the supporting still.
import cn from "classnames";
import { FlagIcon } from "@heroicons/react/24/solid";
import { InvestigationLead } from "../../db/schema";
import { LEAD_CATEGORY_LABELS, SEVERITY_CLASSES } from "./api";
import { kindStyle } from "../common/video/types";

export type PopoverItem = {
  id: string;
  severity?: number;
  label: string;
  corner?: React.ReactNode;
  quote: string;
  explanation?: string;
  kind?: string;
  thumbnailUrl?: string;
};

export type LeadHover = { leads: InvestigationLead[]; items?: PopoverItem[]; x: number; y: number; below: boolean };

export function leadToItem(l: InvestigationLead, corner?: React.ReactNode): PopoverItem {
  return { id: l.id, severity: l.severity, label: LEAD_CATEGORY_LABELS[l.category] || l.category, corner, quote: l.quote_translated || l.quote, explanation: l.explanation || undefined, kind: l.category };
}

const LeadFlagPopover: React.FC<{
  hover: LeadHover;
  corner?: (l: InvestigationLead) => React.ReactNode;
}> = ({ hover, corner }) => {
  const items = hover.items ?? hover.leads.map((l) => leadToItem(l, corner?.(l)));
  return (
    <div
      className="fixed z-50 w-96 rounded-xl border border-base-300 bg-base-100 shadow-xl p-3 pointer-events-none"
      style={{ left: hover.x, top: hover.y, transform: hover.below ? undefined : "translateY(-100%)" }}
    >
      {items.slice(0, 3).map((it, idx) => (
        <div key={it.id} className={cn(idx > 0 && "mt-3 pt-3 border-t border-base-300")}>
          <div className="flex items-center gap-1.5">
            {it.kind && !it.severity ? (
              <span className={cn("size-2.5 rounded-full", kindStyle(it.kind).dot)} />
            ) : (
              <FlagIcon className={cn("size-3.5", (it.severity || 0) >= 4 ? "text-red-500" : "text-amber-500")} />
            )}
            {it.severity != null && <span className={cn("text-[11px] font-semibold px-1.5 rounded", SEVERITY_CLASSES[it.severity])}>sev {it.severity}</span>}
            <span className="text-xs font-semibold text-base-content">{it.label}</span>
            <span className="ml-auto text-[11px] tabular-nums text-base-content/50">{it.corner}</span>
          </div>
          {it.thumbnailUrl && (
            <img src={it.thumbnailUrl} alt="" className="mt-2 w-full h-40 object-cover rounded-lg bg-base-300" />
          )}
          <p className="mt-1.5 text-sm italic text-base-content/90 leading-5">"{it.quote}"</p>
          {it.explanation && <p className="mt-1 text-xs text-base-content/60 leading-5">{it.explanation}</p>}
        </div>
      ))}
      {items.length > 3 && <p className="mt-2 text-[11px] text-base-content/50">+{items.length - 3} more here</p>}
    </div>
  );
};

export default LeadFlagPopover;
